import React, { FC, useMemo } from 'react';
import { Input, InputProps } from 'reactstrap';
import { Field, FieldProps } from 'formik';
import { useTranslation } from '@blocs.i18n';
import Button, { ButtonProps } from '@minecraft.button';
import Icon from '@minecraft.icon';
import { FlexRow, BodyText } from '@minecraft.atoms';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';

interface SelectedFileProps {
  file: File;
  removeFile: (fileName: string) => void;
  /**
   * The label placed on the delete button (aria)
   */
  deleteButtonLabel?: string;
}

const SelectedFile: FC<SelectedFileProps> = ({ file, removeFile, deleteButtonLabel }) => {
  const { t } = useTranslation();

  return (
    <FlexRow
      className="cn_mt-4"
      gap="4"
      textColor="link"
      alignItems="center"
      wrap="nowrap"
      data-testid="selected-file-wrapper"
    >
      <FlexRow
        className="cn_atom_px-3 cn_atom_py-2"
        borderRadius="md"
        gap="4"
        bgColor="pageBody"
        alignItems="center"
        wrap="nowrap"
      >
        <Icon name="spicerack-resume" inheritColor />
        <BodyText title={file.name} color="link" truncatableNoWrap fontWeight="medium" lineHeight="ultraCondensed">
          {file.name}
        </BodyText>
      </FlexRow>
      <Button
        aria-label={deleteButtonLabel ?? t('common:button.delete')}
        color="link"
        fixedWidth={false}
        onClick={() => removeFile(file?.name)}
      >
        <BodyText color="link">
          <Icon name="trash-1" inheritColor />
        </BodyText>
      </Button>
    </FlexRow>
  );
};

/** Types */
type ReactstrapInputProps = Pick<
  InputProps,
  'bsSize' | 'valid' | 'invalid' | 'innerRef' | 'plaintext' | 'addon' | 'type'
>;
type HtmlInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'value' | 'multiple' | 'type' | 'id'
>;

export type FileInputValue = File | File[] | null;

export type FileInputProps = ReactstrapInputProps &
  HtmlInputProps & {
    multiple?: boolean;
    onChange?: (selectedFiles: FileInputValue) => void;
    value: FileInputValue;
    buttonProps?: Omit<ButtonProps, 'onClick' | 'as' | 'type'>;
    buttonLabel: string;
    deleteButtonLabel?: string;
    id: string;
    testId?: string;
    qaId?: string;
  };

const getSelectedFiles = (fileList: FileList): File[] => {
  const selectedFiles = [];
  for (let i = 0; i < fileList.length; i += 1) {
    selectedFiles.push(fileList.item(i));
  }

  return selectedFiles;
};

// basic controlled component
export const FileInput: FC<FileInputProps> = ({
  multiple = false,
  value,
  onChange,
  buttonProps = {} as ButtonProps,
  buttonLabel,
  className,
  deleteButtonLabel,
  id,
  ...inputProps
}) => {
  const testIdBase = inputProps?.testId || `${inputProps.name}-file-input`;
  const qaIdBase = inputProps?.qaId || `${inputProps.name}-file-input`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = getSelectedFiles(e.currentTarget.files);

    if (multiple) {
      onChange(newFiles);
    } else {
      onChange(newFiles?.[0]);
    }
  };

  const handleOpenFileSelect = () => {
    const fileInput = document.getElementById(id);

    if (id && fileInput) {
      fileInput.click();
    }
  };

  const selectedFiles = useMemo(() => {
    const valueArr = Array.isArray(value) ? value : [value];

    return valueArr.filter((v) => v instanceof File);
  }, [value]);

  const removeFile = (fileName: string) => {
    if (multiple) {
      onChange(selectedFiles.filter((f) => f.name !== fileName));
    } else {
      onChange(null);
    }
  };

  return (
    <div data-testid={`${testIdBase}-wrapper`}>
      <Input
        {...inputProps}
        id={id}
        data-testid={testIdBase}
        data-qa-id={qaIdBase}
        className={['visually-hidden'].concat(className).join(' ')}
        multiple={multiple}
        onChange={handleChange}
        value=""
        type="file"
      />
      <Button
        type="button"
        outlined
        $isTall
        {...buttonProps}
        className={['cn_mt-4'].concat(buttonProps?.className).join(' ')}
        data-testid={buttonProps?.testId || `${inputProps.name}-file-input-btn`}
        data-qa-id={buttonProps?.qaId || `${inputProps.name}-file-input-btn`}
        onClick={handleOpenFileSelect}
      >
        {buttonLabel}
      </Button>
      {selectedFiles.map((file) => (
        <SelectedFile
          key={`${file.name}_${file.lastModified}`}
          file={file}
          removeFile={removeFile}
          deleteButtonLabel={deleteButtonLabel}
        />
      ))}
    </div>
  );
};

export interface WrappedFileInputProps {
  wrapperProps?: FieldWrapperProps;
  inputProps: FileInputProps;
}

// component with standard form-field wrapper
export const WrappedFileInput: FC<WrappedFileInputProps> = ({ wrapperProps = {}, inputProps }) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <FileInput {...inputProps} />
    </FieldWrapper>
  );
};

type ControlledFileInputProps = Omit<
  FileInputProps,
  'onChange' | 'value' | 'onBlur' | 'name' | 'isValid' | 'isInvalid'
>;

interface FormikFileInputProps {
  inputProps: ControlledFileInputProps;
  wrapperProps?: FieldWrapperProps;
}

export type ConnectedFileInputProps = FormikFileInputProps & { name: string };

export const ConnectedFileInput: FC<FormikFileInputProps & { name: string }> = ({ name, wrapperProps, inputProps }) => (
  <Field name={name}>
    {({ field: { value: formikValue }, form: { errors, setFieldValue } }: FieldProps<FileInputValue>) => {
      const hasError = errors?.[name];
      const fieldValidation = hasError ? { invalid: true } : {};
      const wrapperValidation = hasError
        ? { validation: { valid: !hasError, message: errors?.[name]?.toString() } }
        : {};

      const onChange = (newVal: File | File[]) => {
        setFieldValue(name, newVal);
      };

      // this prevents the console warning about switching between
      // controlled and uncontrolled input types
      let inputValue = formikValue;

      if (inputValue === undefined) {
        inputValue = null;
      }

      return (
        <WrappedFileInput
          wrapperProps={{ ...wrapperProps, ...wrapperValidation, id: name }}
          inputProps={{ ...inputProps, ...fieldValidation, value: inputValue, name, onChange }}
        />
      );
    }}
  </Field>
);
